import mainStyle from './mainStyle.module.scss';

import { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import useZustandStore from "zustandStore";

import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

import BinIcon from 'assets/images/icons/icn_table_cell_Bin.svg';
import { ReactComponent as BtnUpIcon } from 'assets/images/icons/icn_button_up_light.svg';
import { ReactComponent as BtnDownIcon } from 'assets/images/icons/icn_button_down_light.svg';
import { ReactComponent as AddIcon } from 'assets/images/icons/icn_btn_add.svg';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Const
import { captivePortalConfig, defaultIsLoading, defaultAlertInfo } from './const';

// Components
import DrawerField from 'components/Drawer';
import SearchInput from 'components/SearchInput';
import PaginationField from 'components/Pagination';
import GlobalAlert from 'components/Notification';
import { SolidButton } from 'components/buttons/SolidButton';
import { SolidButton as SolidRectangleButton } from 'components/buttons/SolidRectangleButton';
import Loading from './components/Loading';

import SplashPageEditor from './SplashPageEditor';
import CaptivePortalTable from './captivePortalTable';
import RemoveProfileModal from './Modal/removeProfileModal';
import RemoveCaptivePortalModal from './Modal/removeCaptivePortalModal';
import AddCaptivePortalModal from './Modal/addCaptivePortalModal';

// const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;
const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const StyledDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: theme.spacing(1),
  padding: '10px 20px',
  width: '251px',
  backgroundColor: theme.palette.primary.lightBlue,
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
  borderRadius: 4,
  zIndex: 10,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-8px',
    left: '14px',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${theme.palette.primary.lightBlue}`,
  },
}));

const CaptivePortal = ({ showEditGeneral }) => {
  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();

  const [uuids] = useState(() => ({
    readCaptivePortal: uuidv4(),
    removeCaptivePortal: uuidv4(),
    readUserProfile: uuidv4(),
    readRadiusProfile: uuidv4(),
    readLdapProfile: uuidv4(),
    readWhitelist: uuidv4(),
    readDefaultSplashPageConfig: uuidv4(),
  }));

  const [openAction, setOpenAction] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openRemoveProfileModal, setOpenRemoveProfileModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditorModal, setOpenEditorModal] = useState({ mode: 'edit', open: false });

  const [hasChecked, setHasChecked] = useState(false);
  const [isSelectedCount, setIsSelectedCount] = useState(0);
  const [selectedRowItem, setSelectedRowItem] = useState(null);
  const [selectedProfileItem, setSelectedProfileItem] = useState(null);

  const [originalTableData, setOriginalTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [formData, setFormData] = useState(cloneDeep(captivePortalConfig));
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isLoading, setIsLoading] = useState(structuredClone(defaultIsLoading));
  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));

  const [defaultSplashPageConfig, setDefaultSplashPageConfig] = useState(null);

  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const isUnityPro = serviceStatus?.services.unityPro;

  const breadcrumbs = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;

  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) {
      orgId = orgsId;
    }
  } else {
    orgId = realmId;
  }

  const closeRemoveModal = () => {
    setOpenRemoveModal(false);
    setOpenAction(false);
    setIsSelectedCount(0);
    setTableData(prev => prev.map(item => ({ ...item, checked: false })));
  };

  const clickRemoveBtn = () => {
    setIsLoading(prev => {
      return {
        ...prev,
        removeCaptivePortal: true,
      };
    });

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals.delete`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals.delete`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    const removeIdList = tableData.filter(item => item.checked).map(item => item.id);

    const payload = {
      captivePortalIds: removeIdList,
    };

    sendRequest(apiKey, uuids.removeCaptivePortal, restfulParams, payload);

    setOpenRemoveModal(false);
    setOpenAction(false);
  };

  const clickRemoveProfileBtn = isRemoveProfileId => {
    if (isRemoveProfileId) {
      setIsLoading(prev => {
        return {
          ...prev,
          removeCaptivePortal: true,
        };
      });

      const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
      const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

      const apiConfig = {
        org: `${baseUrl}/settings/captivePortals.delete`,
        site: `${baseUrl}/sites/:siteId/settings/captivePortals.delete`,
      };

      const restfulParamsConfig = {
        standalone: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
        nonStandalone: {
          entId: auth.realm?.id,
          orgId: auth.orgs?.id,
          siteId: auth.sites?.id,
        },
      };

      const apiKey = apiConfig[auth.scopeLevel];
      const mode = standalone ? 'standalone' : 'nonStandalone';
      const restfulParams = restfulParamsConfig[mode];

      const payload = {
        captivePortalIds: [],
      };
      payload.captivePortalIds.push(isRemoveProfileId);

      sendRequest(apiKey, uuids.removeCaptivePortal, restfulParams, payload);
    }

    setDrawerOpen(false);
    setOpenRemoveProfileModal(false);
    setSelectedRowItem(null);
  };

  const changeSearchInput = newValue => {
    const tmpValue = (newValue || '').toLowerCase();
    setSearchInput(newValue || '');

    const tmpList = cloneDeep(originalTableData);
    if (tmpList?.length > 0) {
      const result = tmpList.filter(item => item.captivePortalName.toLowerCase().includes(tmpValue));
      setCurrentPage(0);
      setTableData(result);
    }
  };

  const parseCaptivePortalList = allData => {
    const finalList = [];
    if (allData?.length > 0) {
      allData.forEach(item => {
        finalList.push({
          config: item,
          id: item.id,
          checked: false,
          captivePortalName: item.name,
          authTypeLabel: captivePortalConfig.authTypeList.find(authTypeItem => authTypeItem.value === item.authType)?.label || '',
          authType: item.authType,
        });
      });
    }
    return finalList;
  };

  const reloadPage = data => {
    const isTableData = parseCaptivePortalList(data);
    setTableData(isTableData);
    setOriginalTableData(isTableData);
  };

  const initCaptivePortal = () => {
    setCurrentPage(0);
    setIsLoading(prev => {
      return {
        ...prev,
        readCaptivePortal: true,
      };
    });

    if (hasManageOrg) {
      sendRequest('/ents/:entId/orgs/:orgId/sites/:siteId/settings/captivePortals.read', uuids.readCaptivePortal, { entId, orgId, siteId });
    } else {
      sendRequest('/orgs/:orgId/sites/:siteId/settings/captivePortals.read', uuids.readCaptivePortal, { orgId, siteId });
    }
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    initCaptivePortal();
  }, []);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readCaptivePortal)) return;

    const captivePortalResponse = getData(uuids.readCaptivePortal);
    if (captivePortalResponse) {
      if (checkMockup) {
        if (captivePortalResponse && captivePortalResponse?.data) {
          reloadPage(captivePortalResponse.data);
        }
      } else {
        if (captivePortalResponse.hasOwnProperty('code') && captivePortalResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: captivePortalResponse?.message,
          });
        } else {
          reloadPage(captivePortalResponse.data);
        }
      }

      setIsLoading(prev => {
        return {
          ...prev,
          readUserProfile: true,
          readRadiusProfile: true,
          readLdapProfile: true,
          readWhitelist: true,
          readDefaultSplashPageConfig: true,
        };
      });

      (async () => {
        try {
          const requestList = [
            {
              apiKey: hasManageOrg ? '/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileUserAccounts.read' : '/orgs/:orgId/sites/:siteId/settings/profileUserAccounts.read',
              requestUuid: uuids.readUserProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg ? '/ents/:entId/orgs/:orgId/sites/:siteId/settings/radiusProfile.read' : '/orgs/:orgId/sites/:siteId/settings/radiusProfile.read',
              requestUuid: uuids.readRadiusProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg ? '/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileLdap.read' : '/orgs/:orgId/sites/:siteId/settings/profileLdap.read',
              requestUuid: uuids.readLdapProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg ? '/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileMacAddrs.read' : '/orgs/:orgId/sites/:siteId/settings/profileMacAddrs.read',
              requestUuid: uuids.readWhitelist,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: '/system/defaultSplashPageConfig.read',
              requestUuid: uuids.readDefaultSplashPageConfig,
              restfulPlaceholders: { entId, orgId, siteId },
            },
          ];

          const { getSendBatchData } = await sendBatch(requestList);

          // User Profile response
          const userProfileResp = getSendBatchData({ requestUuid: uuids.readUserProfile });
          if (userProfileResp) {
            const originUserProfiles = userProfileResp?.data;
            if (!checkMockup) {
              if (userProfileResp.hasOwnProperty('code') && userProfileResp?.code !== 200) {
                setAlertInfo({ open: true, severity: 'error', message: userProfileResp?.message });
              }
            }

            if (originUserProfiles) {
              setFormData(prev => {
                const tmp = cloneDeep(prev);
                tmp.userProfileList = cloneDeep(captivePortalConfig.userProfileList);
                if (originUserProfiles?.length > 0) {
                  originUserProfiles.forEach(item => {
                    tmp.userProfileList.push({ label: item.name, value: item.id, isActive: false });
                  });
                }
                return tmp;
              });
            }
          }

          // RADIUS Profile response
          const radiusProfileResp = getSendBatchData({ requestUuid: uuids.readRadiusProfile });
          if (radiusProfileResp) {
            const originRadius = radiusProfileResp?.data;
            if (!checkMockup) {
              if (radiusProfileResp.hasOwnProperty('code') && radiusProfileResp?.code !== 200) {
                setAlertInfo({ open: true, severity: 'error', message: radiusProfileResp?.message });
              }
            }

            if (originRadius) {
              setFormData(prev => {
                const tmp = cloneDeep(prev);
                tmp.radiusProfileList = cloneDeep(captivePortalConfig.radiusProfileList);
                if (originRadius?.length > 0) {
                  originRadius.forEach(item => {
                    tmp.radiusProfileList.push({ label: item.name, value: item.id, isActive: false });
                  });
                }
                return tmp;
              });
            }
          }

          // LDAP Profile response
          const ldapProfileResp = getSendBatchData({ requestUuid: uuids.readLdapProfile });
          if (ldapProfileResp) {
            const originLdap = ldapProfileResp?.data;
            if (!checkMockup) {
              if (ldapProfileResp.hasOwnProperty('code') && ldapProfileResp?.code !== 200) {
                setAlertInfo({ open: true, severity: 'error', message: ldapProfileResp?.message });
              }
            }

            if (originLdap) {
              setFormData(prev => {
                const tmp = cloneDeep(prev);
                tmp.ldapProfileList = cloneDeep(captivePortalConfig.ldapProfileList);
                if (originLdap?.length > 0) {
                  originLdap.forEach(item => {
                    tmp.ldapProfileList.push({ label: item.name, value: item.id, isActive: false });
                  });
                }
                return tmp;
              });
            }
          }

          // Whitelist (MAC Address) response
          const whitelistResp = getSendBatchData({ requestUuid: uuids.readWhitelist });
          if (whitelistResp) {
            const originWhitelist = whitelistResp?.data;
            if (!checkMockup) {
              if (whitelistResp.hasOwnProperty('code') && whitelistResp?.code !== 200) {
                setAlertInfo({
                  open: true,
                  severity: 'error',
                  message: whitelistResp?.message,
                });
              }
            }

            if (originWhitelist) {
              setFormData(prev => {
                const tmp = cloneDeep(prev);
                tmp.whitelistList = cloneDeep(captivePortalConfig.whitelistList);
                if (originWhitelist?.length > 0) {
                  originWhitelist.forEach(item => {
                    tmp.whitelistList.push({
                      label: item.name,
                      value: item.id,
                      macListLength: item.macList?.length ?? 0,
                      isActive: false,
                    });
                  });
                }
                return tmp;
              });
            }
          }

          // Default Splash page config response
          const defaultSplashPageConfigResp = getSendBatchData({ requestUuid: uuids.readDefaultSplashPageConfig });
          if (defaultSplashPageConfigResp) {
            const defaultSplashPage = defaultSplashPageConfigResp?.data;
            if (!checkMockup) {
              if (defaultSplashPageConfigResp.hasOwnProperty('code') && defaultSplashPageConfigResp?.code !== 200) {
                setAlertInfo({
                  open: true,
                  severity: 'error',
                  message: defaultSplashPageConfigResp?.message,
                });
              }
            }

            if (defaultSplashPage) {
              setDefaultSplashPageConfig(defaultSplashPage);
            }
          }
        } catch (error) {
          // Nothing to do.
        } finally {
          setIsLoading(prev => {
            return {
              ...prev,
              readUserProfile: false,
              readRadiusProfile: false,
              readLdapProfile: false,
              readWhitelist: false,
              readDefaultSplashPageConfig: false,
            };
          });
        }
      })();
    }

    setIsLoading(prev => {
      return {
        ...prev,
        readCaptivePortal: false,
      };
    });
  }, [dataMap?.[uuids.readCaptivePortal]]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.removeCaptivePortal)) return;

    const captivePortalDeleteResponse = getData(uuids.removeCaptivePortal);

    if (captivePortalDeleteResponse) {
      if (checkMockup) {
        if (captivePortalDeleteResponse && captivePortalDeleteResponse?.data) {
          reloadPage(captivePortalDeleteResponse.data);
        }
      } else {
        if (captivePortalDeleteResponse.hasOwnProperty('code') && captivePortalDeleteResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: captivePortalDeleteResponse?.message,
          });
        } else {
          initCaptivePortal();
        }
      }
    }

    setIsLoading(prev => {
      return {
        ...prev,
        removeCaptivePortal: false,
      };
    });
  }, [dataMap?.[uuids.removeCaptivePortal]]);

  return (
    <>
      {(isLoading.readCaptivePortal ||
        isLoading.removeCaptivePortal ||
        isLoading.readUserProfile ||
        isLoading.readRadiusProfile ||
        isLoading.readLdapProfile ||
        isLoading.readWhitelist ||
        isLoading.readDefaultSplashPageConfig) && <Loading />}

      <Box margin="20px">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            {hasChecked && (
              <>
                <SolidRectangleButton startIcon={openAction ? <BtnUpIcon /> : <BtnDownIcon />} width="100px" height="32px" onClick={() => setOpenAction(!openAction)}>
                  Action
                </SolidRectangleButton>
                {openAction && (
                  <StyledDropdown>
                    <Box className={`${mainStyle['action_item']}`} onClick={() => setOpenRemoveModal(true)}>
                      <img src={BinIcon} alt="icn_table_cell_Bin" />
                      <span>Remove</span>
                    </Box>
                  </StyledDropdown>
                )}
                <Divider sx={{ width: '1px', height: '40px', marginLeft: '18px' }} orientation="vertical" flexItem />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex' }}>
            <Divider sx={{ width: '1px', height: '40px', marginRight: '18px' }} orientation="vertical" flexItem />
            <Box sx={{ marginRight: '18px' }}>
              <SearchInput placeholder="Search by Captive Portal Name" value={searchInput} onChange={newValue => changeSearchInput(newValue)} />
            </Box>
            {showEditGeneral && (
              <SolidButton sx={{ minWidth: 185, height: 32 }} startIcon={<AddIcon />} onClick={() => setOpenCreateModal(true)}>
                Create Captive Portal
              </SolidButton>
            )}
          </Box>
        </Box>

        <CaptivePortalTable
          {...{
            tableData,
            setTableData,
            hasChecked,
            setHasChecked,
            setIsSelectedCount,
            selectedRowItem,
            setSelectedRowItem,
            setDrawerOpen,
            currentPage,
            rowsPerPage,
            setOpenEditorModal,
            showEditGeneral,
            setSelectedProfileItem,
            isUnityPro,
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <PaginationField
            name="captivePortalTable"
            pageCount={tableData?.length > 0 ? Math.max(1, Math.ceil((tableData?.length ?? 0) / rowsPerPage)) : 0}
            pageIndex={currentPage}
            pageSize={rowsPerPage}
            onPageChange={page => setCurrentPage(page)}
            onPageSizeChange={size => setRowsPerPage(size)}
          />
        </Box>
      </Box>

      {drawerOpen && (
        <DrawerField
          width="580px"
          open={drawerOpen}
          handleClose={() => {
            setDrawerOpen(false);
            setSelectedRowItem(null);
          }}
          contentKey={`Settings_CaptivePortal_Drawer_${uuidv4()}`}
          commonContent="settingsCaptivePortal"
          contentProps={{
            drawerOpen,
            setDrawerOpen,
            setSelectedRowItem,
            setOpenRemoveProfileModal,
            tableData,
            originalRowData: selectedRowItem,
            reloadPage,
            initCaptivePortal,
            formData,
            setFormData,
            showEditGeneral,
            isUnityPro,
          }}
        />
      )}

      <AddCaptivePortalModal
        openCreateModal={openCreateModal}
        setOpenCreateModal={setOpenCreateModal}
        tableData={tableData}
        formData={formData}
        setFormData={setFormData}
        reloadPage={reloadPage}
        initCaptivePortal={initCaptivePortal}
        isUnityPro={isUnityPro}
      />

      <RemoveCaptivePortalModal
        openRemoveModal={openRemoveModal}
        closeRemoveModal={closeRemoveModal}
        clickRemoveBtn={clickRemoveBtn}
        isSelectedCount={isSelectedCount}
        showEditGeneral={showEditGeneral}
      />

      <RemoveProfileModal
        openRemoveProfileModal={openRemoveProfileModal}
        setOpenRemoveProfileModal={setOpenRemoveProfileModal}
        clickRemoveProfileBtn={clickRemoveProfileBtn}
        isSelectedItem={selectedRowItem}
      />

      <SplashPageEditor
        openEditorModal={openEditorModal}
        setOpenEditorModal={setOpenEditorModal}
        selectedProfileItem={selectedProfileItem}
        tableData={tableData}
        defaultSplashPageConfig={defaultSplashPageConfig}
        isDefaultConfigLoading={isLoading.readDefaultSplashPageConfig}
        initCaptivePortal={initCaptivePortal}
        showEditGeneral={showEditGeneral}
      />

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </>
  );
};

export default CaptivePortal;
